/**
 * Back-in-stock subscribe form.
 *
 * Custom element (<back-in-stock-form>) that captures an email and posts to
 * Klaviyo's Back-in-Stock Subscriptions client API. Klaviyo records the
 * subscription and emits the "Subscribed to Back in Stock" metric, which is
 * the trigger on the existing "Back In Stock Flow - Standard" flow.
 *
 * Klaviyo API reference:
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=<PUBLIC_KEY>
 *   https://developers.klaviyo.com/en/reference/create_back_in_stock_subscription
 *
 * REQUIRED: paste the Klaviyo Public API Key (Account → API Keys → Public API Key)
 * in the constant below.
 */
(function () {
  // ────────────────────────────────────────────────────────────────
  //  Replace the placeholder below with your Klaviyo Public API Key.
  //  This is the "Public Key" / "Company ID" — NOT a Private Key.
  // ────────────────────────────────────────────────────────────────
  var KLAVIYO_PUBLIC_KEY = 'YnfN3m';
  var KLAVIYO_REVISION = '2024-10-15';

  if (customElements.get('back-in-stock-form')) return;

  class BackInStockForm extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-action="open"]');
      this.form = this.querySelector('.back-in-stock__form');
      this.success = this.querySelector('.back-in-stock__success');
      this.submitBtn = this.querySelector('[data-action="submit"]');
      this.cancelBtn = this.querySelector('[data-action="cancel"]');
      this.emailInput = this.querySelector('.back-in-stock__email');
      this.errorEl = this.querySelector('.back-in-stock__error');

      this.trigger.addEventListener('click', () => this.openForm());
      this.cancelBtn.addEventListener('click', () => this.closeForm());
      this.submitBtn.addEventListener('click', () => this.submit());
      this.emailInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.submit();
        }
      });
    }

    openForm() {
      this.trigger.hidden = true;
      this.form.hidden = false;
      this.emailInput.focus();
    }

    closeForm() {
      this.form.hidden = true;
      this.trigger.hidden = false;
      this.errorEl.hidden = true;
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    async submit() {
      const email = this.emailInput.value.trim();
      if (!this.isValidEmail(email)) {
        this.showError(this.dataset.errorInvalidEmail);
        this.emailInput.focus();
        return;
      }

      if (!KLAVIYO_PUBLIC_KEY || KLAVIYO_PUBLIC_KEY.indexOf('<<') === 0) {
        console.error('[back-in-stock] Klaviyo public key not configured in assets/back-in-stock.js');
        this.showError(this.dataset.errorNotConfigured);
        return;
      }

      // Pull the current variant id at submit time — Shopify re-renders this
      // element on variant change, so dataset is always current.
      const variantId = this.dataset.variantId;
      if (!variantId) {
        this.showError(this.dataset.errorNoVariant);
        return;
      }

      this.submitBtn.classList.add('btn--loading');
      this.submitBtn.disabled = true;
      this.errorEl.hidden = true;

      const url = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(KLAVIYO_PUBLIC_KEY)}`;
      const body = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: { email }
              }
            }
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`
              }
            }
          }
        }
      };

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': KLAVIYO_REVISION,
            'accept': 'application/vnd.api+json'
          },
          body: JSON.stringify(body)
        });

        // Klaviyo returns 202 Accepted on success.
        if (!response.ok && response.status !== 202) {
          let errorText = `(${response.status})`;
          try {
            const errBody = await response.json();
            if (errBody && errBody.errors && errBody.errors[0]) {
              errorText = errBody.errors[0].detail || errBody.errors[0].title || errorText;
            }
          } catch (_) {}
          throw new Error(errorText);
        }

        this.form.hidden = true;
        this.success.hidden = false;
      } catch (err) {
        console.error('[back-in-stock] subscription failed:', err);
        this.showError(this.dataset.errorGeneric);
      } finally {
        this.submitBtn.classList.remove('btn--loading');
        this.submitBtn.disabled = false;
      }
    }
  }

  customElements.define('back-in-stock-form', BackInStockForm);
})();
